import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

interface SessionTimerContextType {
  timeLeft: number;
  isWarning: boolean;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  extendSession: () => Promise<void>;
  logout: (onSaveState?: () => Promise<void>, onSessionExpired?: () => void) => Promise<void>;
  isExtending: boolean;
  formatTime: (seconds: number) => string;
  sessionDuration: number;
  warningTime: number;
}

const SessionTimerContext = createContext<SessionTimerContextType | undefined>(undefined);

interface SessionTimerProviderProps {
  children: React.ReactNode;
  sessionDuration?: number; // in minutes, default 360 (6 hours)
  warningTime?: number; // in minutes before expiry to show warning, default 5
}

export const SessionTimerProvider: React.FC<SessionTimerProviderProps> = ({
  children,
  sessionDuration = 360,
  warningTime = 5
}) => {
  const [timeLeft, setTimeLeft] = useState<number>(sessionDuration * 60);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isExtending, setIsExtending] = useState<boolean>(false);
  const onSaveStateRef = useRef<(() => Promise<void>) | undefined>();
  const onSessionExpiredRef = useRef<(() => void) | undefined>();

  const isWarning = timeLeft <= warningTime * 60;

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const extendSession = async () => {
    setIsExtending(true);
    try {
      axios.defaults.withCredentials = true;
      await axios.post(`${API_BASE}/api/extend-session`);
      setTimeLeft(sessionDuration * 60);
      setShowModal(false);
    } catch (error) {
      console.error('Error extending session:', error);
      setTimeLeft(sessionDuration * 60);
      setShowModal(false);
    } finally {
      setIsExtending(false);
    }
  };

  const logout = useCallback(async (onSaveState?: () => Promise<void>, onSessionExpired?: () => void) => {
    // Save state before logout
    const saveStateFn = onSaveState || onSaveStateRef.current;
    if (saveStateFn) {
      try {
        await saveStateFn();
      } catch (error) {
        console.error('Error saving state before logout:', error);
      }
    }

    // Clear session and redirect
    try {
      axios.defaults.withCredentials = true;
      await axios.post(`${API_BASE}/api/logout`);
    } catch (error) {
      console.error('Error during logout:', error);
    }

    // Clear session storage
    sessionStorage.removeItem('userEmail');
    sessionStorage.removeItem('userProfileData');
    sessionStorage.removeItem('currentApplicationId');
    sessionStorage.removeItem('isLoggedIn');

    const expiredFn = onSessionExpired || onSessionExpiredRef.current;
    if (expiredFn) {
      expiredFn();
    } else {
      window.location.href = '/';
    }
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          logout();
          return 0;
        }
        
        // Show modal when warning time is reached
        if (prev === warningTime * 60) {
          setShowModal(true);
        }
        
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [warningTime, logout]);

  const value: SessionTimerContextType = {
    timeLeft,
    isWarning,
    showModal,
    setShowModal,
    extendSession,
    logout,
    isExtending,
    formatTime,
    sessionDuration,
    warningTime
  };

  return (
    <SessionTimerContext.Provider value={value}>
      {children}
    </SessionTimerContext.Provider>
  );
};

export const useSessionTimer = (): SessionTimerContextType => {
  const context = useContext(SessionTimerContext);
  if (!context) {
    throw new Error('useSessionTimer must be used within a SessionTimerProvider');
  }
  return context;
};

export default SessionTimerContext;
